/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useState } from 'react'
import {
	Table,
	Typography,
	TablePaginationConfig,
	Tooltip,
	Button,
	Drawer,
	Tag,
	Divider,
	Row,
	Col,
	Space,
	DatePicker,
	Select,
} from 'antd'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import {
	CLIENT_WALLET_INITIAL_PAGE_SIZE,
	getWalletTransactionsAction,
	setPageSize,
} from 'slices/clientWalletTransactions'
import moment, { Moment } from 'moment'
import AddDeductMoneyForm from 'component/Wallet/AddDeductMoneyForm'
import { ClientProfile, ClientWalletTransaction } from 'types'
import { RangeValue } from 'rc-picker/lib/interface'
import getWalletTransactions from 'api/clients/getWalletTransactions'
import downloadUsingAPI from 'utils/downloadUsingAPI'
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons'
import addCurrencySymbol from 'utils/addCurrencySymbol'

const { Title } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

/**
 * Hardcoded size of the table
 */

/**
 * Wallet Transaction Table component props
 */

interface PropTypes {
	clientProfile: ClientProfile | null
}

const WalletTable: React.FC<PropTypes> = ({ clientProfile }) => {
	const [transactionFormDrawer, setTransactionFormDrawer] = useState(false)
	const dispatch = useAppDispatch()
	const { records, currentPage, totalCount, loading } = useAppSelector(
		state => state.walletTransaction
	)

	const currency =
		useAppSelector(
			state => state.singleClient.clientProfile?.currency_selected
		) ?? 'INR'
	const size = useAppSelector(state => state.walletTransaction.pageSize)

	const [dateRange, setDateRange] = useState<RangeValue<Moment>>(null)
	const [walletType, setWalletType] = useState<null | 'CREDIT' | 'DEBIT'>(null)
	const [exporting, setExporting] = useState(false)

	/**
	 * Open Drawer for Wallet Transaction Form
	 */
	const openTransactionFormDrawer = () => {
		setTransactionFormDrawer(true)
	}

	/**
	 * Close Drawer for Wallet Transaction Form
	 */
	const closeTransactionFormDrawer = () => {
		setTransactionFormDrawer(false)
	}

	/**
	 * Columns for Table of Wallet Transaction
	 */

	const transactionColumns = [
		{
			title: `Amount (${currency})`,
			dataIndex: 'amount',
			render: (amount: number, value: ClientWalletTransaction) => (
				<>
					{addCurrencySymbol(amount, value.txn_currency ?? currency)}{' '}
					<Tooltip title={<pre>{JSON.stringify(value.cost, null, 2)}</pre>}>
						<InfoCircleOutlined />
					</Tooltip>
				</>
			),
		},
		{
			title: 'Type',
			dataIndex: 'type',
			render: (type: string) =>
				// eslint-disable-next-line no-nested-ternary
				type === 'DEBIT' ? (
					<Tag color='red'>{type}</Tag>
				) : type === 'CREDIT' ? (
					<Tag color='green'>{type}</Tag>
				) : (
					'--'
				),
		},
		{
			title: 'Created At',
			dataIndex: 'createdAt',
			width: 150,
			render: (createdAt: string) =>
				moment(createdAt).format('DD-MM-YYYY HH:mm:ss'),
		},
		{
			title: 'Remarks',
			dataIndex: 'remarks',
		},
		{
			title: 'Module',
			dataIndex: 'module',
		},
		{
			title: 'Visible',
			dataIndex: 'visible',
			render: (visible: boolean) => (visible ? 'Yes' : 'No'),
		},
		{
			title: 'User Name',
			dataIndex: 'meta',
			render: (meta: { [key: string]: any }) =>
				meta?.user ? meta.user.name : '--',
		},
	]

	/**
	 * Wallet data for single client
	 */
	const walletData = [
		{
			active: clientProfile?.wallet?.active ? 'Yes' : 'No',
			createdAt: clientProfile?.wallet
				? moment(clientProfile?.wallet.createdAt).format('DD-MM-YYYY HH:mm:ss')
				: null,
			credit: clientProfile?.wallet?.amount?.credit,
			reserve: clientProfile?.wallet?.amount?.reserve,
		},
	]

	/**
	 *  Wallet Columns for Table of Single client
	 */

	const walletColumns = [
		{
			title: 'Active',
			dataIndex: 'active',
		},
		{
			title: 'Created At',
			dataIndex: 'createdAt',
		},
		{
			title: 'Credit',
			dataIndex: 'credit',
			render: (credit: number) => addCurrencySymbol(credit, currency),
		},
		{
			title: 'Reserve',
			dataIndex: 'reserve',
		},
	]

	/**
	 * Pagination config for the table
	 */
	const pagination = {
		current: currentPage,
		total: totalCount,
		pageSize: size,
	}

	useEffect(() => {
		if (!clientProfile?._id) {
			return
		}
		dispatch(setPageSize(CLIENT_WALLET_INITIAL_PAGE_SIZE))

		const options = {
			page: 1,
			size: pagination.pageSize,
			id: clientProfile._id,
			...(dateRange &&
				dateRange[0] &&
				dateRange[1] && {
					startDate: dateRange[0].startOf('D').toJSON(),
					endDate: dateRange[1].endOf('D').toJSON(),
				}),
			...(walletType && {
				type: walletType,
			}),
		}
		dispatch(getWalletTransactionsAction(options))
	}, [clientProfile?._id, dateRange, walletType])

	/**
	 * Handles pagination change by changing the current page in the store.
	 *
	 * @param page - Pagination object from the table
	 */

	const handlePageChange = (page: TablePaginationConfig) => {
		if (!clientProfile?._id) {
			return
		}
		dispatch(setPageSize(page.pageSize || size))

		const options = {
			page: page.current || 1,
			size: page.pageSize || size,
			id: clientProfile._id,
			...(dateRange &&
				dateRange[0] &&
				dateRange[1] && {
					startDate: dateRange[0].startOf('D').toJSON(),
					endDate: dateRange[1].endOf('D').toJSON(),
				}),
			...(walletType && {
				type: walletType,
			}),
		}
		dispatch(getWalletTransactionsAction(options))
	}

	/**
	 * Call on success of New Invoice form submit
	 * @param val : val will be true on form success
	 */
	const onSuccessWalletTransaction = (val: boolean) => {
		if (val && clientProfile?._id) {
			setTransactionFormDrawer(false)
			const options = {
				page: 1,
				size: pagination.pageSize,
				id: clientProfile._id,
				...(dateRange &&
					dateRange[0] &&
					dateRange[1] && {
						startDate: dateRange[0].startOf('D').toJSON(),
						endDate: dateRange[1].endOf('D').toJSON(),
					}),
				...(walletType && {
					type: walletType,
				}),
			}
			dispatch(getWalletTransactionsAction(options))
		}
	}

	const handleRangeChange = (values: RangeValue<Moment>) => {
		setDateRange(values)
	}

	const handleTypeChange = (value: null | 'DEBIT' | 'CREDIT') => {
		setWalletType(value)
	}

	const handleExport = async () => {
		if (!clientProfile?._id) {
			return
		}

		try {
			setExporting(true)

			const options = {
				id: clientProfile._id,
				...(dateRange &&
					dateRange[0] &&
					dateRange[1] && {
						startDate: dateRange[0].startOf('D').toJSON(),
						endDate: dateRange[1].endOf('D').toJSON(),
					}),
				...(walletType && {
					type: walletType,
				}),
				should_export: true,
			}

			const csvFile = (await getWalletTransactions(options)) as unknown as string

			downloadUsingAPI(
				csvFile,
				`${clientProfile.organizationName}-wallet-transactions${
					walletType ? `-${walletType}` : ''
				}${
					dateRange && dateRange[0] && dateRange[1]
						? `-range-${dateRange[0].format('DD-MM-YYYY')}-to-${dateRange[1].format(
								'DD-MM-YYYY'
						  )}`
						: ''
				}.csv`
			)
		} finally {
			setExporting(false)
		}
	}

	return (
		<>
			<Row gutter={[24, 24]} justify='space-between'>
				<Col>
					<Title level={5}>Wallet Details</Title>
				</Col>

				<Col>
					<Tooltip placement='top' title='Add/Deduct Money from wallet'>
						<Button type='primary' onClick={openTransactionFormDrawer}>
							Create Transaction
						</Button>
					</Tooltip>
				</Col>

				<Col span={24}>
					<Table
						columns={walletColumns}
						dataSource={walletData}
						loading={loading}
						pagination={false}
					/>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[24, 24]} justify='space-between'>
				<Col>
					<Title level={5}>Wallet Transactions</Title>
				</Col>
				<Col>
					<Space size='large'>
						<RangePicker value={dateRange} onChange={handleRangeChange} />

						<Select
							value={walletType}
							onChange={handleTypeChange}
							style={{ width: '120px' }}
							placeholder='Select Type'
							allowClear
						>
							<Option value='CREDIT'>Credit</Option>
							<Option value='DEBIT'>Debit</Option>
						</Select>

						<Tooltip placement='top' title='Export Current Transactions'>
							<Button
								type='primary'
								onClick={handleExport}
								loading={exporting}
								icon={<DownloadOutlined />}
							>
								Export
							</Button>
						</Tooltip>
					</Space>
				</Col>
				<Col span={24}>
					<Table
						columns={transactionColumns}
						rowKey={record => record._id}
						dataSource={records}
						loading={loading}
						pagination={{
							...pagination,
							position: ['bottomCenter'],
						}}
						onChange={handlePageChange}
						scroll={{ x: 'fit-content' }}
					/>
				</Col>
			</Row>

			<Drawer
				title='Add/Deduct Money from wallet'
				placement='right'
				width='33vw'
				closable
				visible={transactionFormDrawer}
				onClose={closeTransactionFormDrawer}
				destroyOnClose
			>
				{clientProfile && (
					<AddDeductMoneyForm
						clientId={clientProfile._id}
						onSuccess={onSuccessWalletTransaction}
					/>
				)}
			</Drawer>
		</>
	)
}

export default WalletTable
